import React, { useState } from 'react';
import { X, Sparkles, ShieldCheck, CreditCard, CheckCircle2, ShoppingBag, ArrowLeft, Printer, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { STRIPE_CONFIG, isStripeConfigured } from '../config/stripe';
import confetti from 'canvas-confetti';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({ isOpen, onClose }) => {
  const {
    cartItems,
    selectedAddOns,
    subtotal,
    discountAmount,
    shippingFee,
    taxAmount,
    finalTotal,
    clearCart,
  } = useCart();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'CA',
    zip: '',
    cardNumber: '•••• •••• •••• 4242',
    expDate: '12/28',
    cvv: '•••',
    notes: '',
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [completedOrderId, setCompletedOrderId] = useState('');
  const [completedOrderDate, setCompletedOrderDate] = useState('');

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProcessOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    // If Stripe Payment Link is configured, redirect user to Stripe
    if (STRIPE_CONFIG.stripePaymentLink) {
      window.location.href = STRIPE_CONFIG.stripePaymentLink;
      return;
    }

    // Otherwise, simulate instantaneous verified checkout flow
    setTimeout(() => {
      const orderId = `KYLEE-${Math.floor(100000 + Math.random() * 900000)}`;
      const orderDate = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

      setCompletedOrderId(orderId);
      setCompletedOrderDate(orderDate);
      setIsProcessing(false);
      setOrderComplete(true);

      // Trigger Celebration Confetti
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.5 },
        colors: ['#D4AF37', '#F472B6', '#C084FC', '#2DD4BF', '#FEF08A'],
      });

      clearCart();
    }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200">
      <div
        className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl border border-[#EADFCF] my-auto max-h-[92vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-5 sm:p-6 border-b border-[#F5EBE0] bg-[#FFFDF9] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#D4AF37] to-[#F472B6] p-0.5 shadow-xs">
              <img src="/logo.png" alt="Logo" className="w-full h-full rounded-full object-cover bg-white" />
            </div>
            <div>
              <h2 className="font-serif text-lg sm:text-xl font-bold text-[#292524]">
                {orderComplete ? 'Order Confirmation' : 'Secure Checkout'}
              </h2>
              <p className="text-[11px] text-[#78716C]">Nailed It • Handcrafted Press-On Nails by Kylee</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close Checkout"
            className="p-1.5 rounded-full hover:bg-[#FAF5EE] text-[#78716C] hover:text-[#292524]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {orderComplete ? (
          /* Order Complete / Receipt Screen */
          <div className="p-6 sm:p-10 space-y-6 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-[#FEF08A] to-[#D1FAE5] text-[#059669] flex items-center justify-center mx-auto text-3xl shadow-lg border border-[#A7F3D0]">
              ✓
            </div>

            <div className="space-y-2 max-w-lg mx-auto">
              <span className="text-xs font-extrabold uppercase tracking-widest text-[#10B981] bg-[#D1FAE5] px-3 py-1 rounded-full">
                Payment Successful • Order Confirmed
              </span>
              <h3 className="font-serif text-3xl font-extrabold text-[#292524]">
                Thank You, {formData.name || 'Friend'}! 💖
              </h3>
              <p className="text-xs sm:text-sm text-[#57534E] leading-relaxed">
                Kylee has received your order and will start hand-painting your custom nail set with love and salon gel!
              </p>
            </div>

            {/* Receipt Summary Card */}
            <div className="max-w-md mx-auto p-6 rounded-2xl bg-[#FFFDF9] border border-[#EADFCF] text-left space-y-4 shadow-xs">
              <div className="flex justify-between items-center border-b border-[#F5EBE0] pb-3 text-xs">
                <div>
                  <span className="text-[#78716C] block">Order Number:</span>
                  <strong className="font-mono font-bold text-[#292524]">{completedOrderId}</strong>
                </div>
                <div className="text-right">
                  <span className="text-[#78716C] block">Order Date:</span>
                  <strong className="text-[#292524]">{completedOrderDate}</strong>
                </div>
              </div>

              <div className="space-y-1.5 text-xs text-[#57534E]">
                <div className="flex justify-between">
                  <span>Customer Email:</span>
                  <span className="font-medium text-[#292524]">{formData.email || 'customer@example.com'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping To:</span>
                  <span className="font-medium text-[#292524] text-right">
                    {formData.address || '123 Main St'}, {formData.city || 'Anytown'}, {formData.state} {formData.zip}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-bold text-[#292524] pt-2 border-t border-[#F5EBE0]">
                  <span>Total Paid:</span>
                  <span className="text-[#B45309] font-serif text-base">${finalTotal.toFixed(2)}</span>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-[#FAF5EE] text-[11px] text-[#78716C] space-y-1">
                <div className="flex items-center gap-1 font-bold text-[#292524]">
                  <Sparkles className="w-3 h-3 text-[#D4AF37]" />
                  <span>Estimated Delivery: 4-6 Business Days</span>
                </div>
                <p>Tracking information will be sent directly to your email once Kylee finishes hand-painting and boxing your order.</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                onClick={() => window.print()}
                className="px-5 py-2.5 rounded-xl bg-[#FAF5EE] hover:bg-[#F3EBE0] text-[#44403C] text-xs font-semibold border border-[#E7DEC8] flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                <span>Print Receipt</span>
              </button>
              <button
                onClick={onClose}
                className="px-8 py-3 rounded-xl text-white font-bold text-xs gold-shimmer-btn shadow-md"
              >
                Continue Browsing
              </button>
            </div>
          </div>
        ) : (
          /* Main Checkout Form */
          <form onSubmit={handleProcessOrder} className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Left Column: Customer & Shipping & Payment Info */}
            <div className="lg:col-span-7 space-y-6">
              
              {/* Customer Contact */}
              <div className="space-y-3">
                <h3 className="font-serif text-base font-bold text-[#292524] flex items-center gap-2">
                  <span>1. Contact Information</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-[#57534E]">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Jane Doe"
                      className="w-full px-3 py-2 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] text-xs text-[#292524] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-[#57534E]">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="jane@example.com"
                      className="w-full px-3 py-2 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] text-xs text-[#292524] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="space-y-3">
                <h3 className="font-serif text-base font-bold text-[#292524]">
                  2. Shipping Address
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-[#57534E]">Street Address *</label>
                    <input
                      type="text"
                      name="address"
                      required
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Blossom Lane"
                      className="w-full px-3 py-2 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] text-xs text-[#292524] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-[#57534E]">City *</label>
                      <input
                        type="text"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Dallas"
                        className="w-full px-3 py-2 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] text-xs text-[#292524] focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-[#57534E]">State *</label>
                      <input
                        type="text"
                        name="state"
                        required
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="TX"
                        className="w-full px-3 py-2 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] text-xs text-[#292524] focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-[#57534E]">ZIP Code *</label>
                      <input
                        type="text"
                        name="zip"
                        required
                        value={formData.zip}
                        onChange={handleInputChange}
                        placeholder="75001"
                        className="w-full px-3 py-2 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] text-xs text-[#292524] focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Stripe Payment Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-base font-bold text-[#292524] flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-[#D4AF37]" />
                    <span>3. Payment Details (Stripe Ready)</span>
                  </h3>
                  <span className="text-[10px] bg-[#D1FAE5] text-[#065F46] font-bold px-2 py-0.5 rounded-full border border-[#A7F3D0]">
                    256-bit SSL
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-[#FAF5EE] border border-[#E7DEC8] space-y-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-[#57534E]">Card Number</label>
                    <input
                      type="text"
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={handleInputChange}
                      placeholder="4242 4242 4242 4242"
                      className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7DEC8] text-xs font-mono text-[#292524]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-[#57534E]">Expires (MM/YY)</label>
                      <input
                        type="text"
                        name="expDate"
                        value={formData.expDate}
                        onChange={handleInputChange}
                        placeholder="MM/YY"
                        className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7DEC8] text-xs text-[#292524]"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-[#57534E]">Security CVC</label>
                      <input
                        type="text"
                        name="cvv"
                        value={formData.cvv}
                        onChange={handleInputChange}
                        placeholder="123"
                        className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7DEC8] text-xs text-[#292524]"
                      />
                    </div>
                  </div>
                </div>

                {/* Stripe Live Linking Helper Info */}
                <div className="p-3 rounded-xl bg-[#FEF9C3]/50 border border-[#FDE047] text-[11px] text-[#854D0E] flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-[#D97706] shrink-0 mt-0.5" />
                  <span>
                    <strong>Stripe Ready:</strong> You can link live Stripe payments by setting <code className="bg-white/80 px-1 py-0.5 rounded font-mono text-[10px]">STRIPE_SECRET_KEY</code> in Cloudflare or providing your Stripe Payment Link. Click "Complete Order" below to test the full purchase flow!
                  </span>
                </div>
              </div>

            </div>

            {/* Right Column: Order Summary Sidebar */}
            <div className="lg:col-span-5 bg-[#FAF5EE]/70 rounded-2xl p-5 border border-[#EADFCF] flex flex-col justify-between space-y-4">
              <div className="space-y-4">
                <h3 className="font-serif text-base font-bold text-[#292524]">
                  Order Summary ({cartItems.reduce((s, i) => s + i.quantity, 0)} Items)
                </h3>

                {/* Items List */}
                <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
                  {cartItems.map((item) => (
                    <div key={item.cartId} className="flex justify-between text-xs pb-2 border-b border-[#E7DEC8]/60">
                      <div className="space-y-0.5">
                        <span className="font-bold text-[#292524] block">{item.product.name}</span>
                        <span className="text-[11px] text-[#78716C] block">
                          Qty: {item.quantity} • {item.shape} • {item.length} • Size {item.sizePreset}
                        </span>
                      </div>
                      <span className="font-serif font-bold text-[#B45309]">
                        ${(item.unitPrice * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  {selectedAddOns.map((addon) => (
                    <div key={addon.id} className="flex justify-between text-xs pb-1">
                      <span className="text-[#57534E]">{addon.name}</span>
                      <span className="font-serif font-semibold text-[#854D0E]">
                        +${addon.price.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Calculation breakdown */}
                <div className="space-y-1.5 text-xs text-[#57534E] pt-2 border-t border-[#E7DEC8]">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-[#10B981]">
                      <span>Discount:</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Shipping:</span>
                    <span>{shippingFee === 0 ? 'FREE' : `$${shippingFee.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Estimated Tax:</span>
                    <span>${taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-base font-serif font-extrabold text-[#292524] pt-2 border-t border-[#E7DEC8]">
                    <span>Total:</span>
                    <span className="text-[#B45309]">${finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Submit CTA */}
              <div className="pt-4 space-y-2">
                <button
                  type="submit"
                  disabled={isProcessing || cartItems.length === 0}
                  className="w-full py-4 rounded-2xl text-white font-bold text-sm gold-shimmer-btn shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 active:scale-98 disabled:opacity-50"
                >
                  {isProcessing ? (
                    <span>Processing Secure Payment...</span>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" />
                      <span>Complete Order • ${finalTotal.toFixed(2)}</span>
                    </>
                  )}
                </button>
                <div className="flex items-center justify-center gap-1 text-[10px] text-[#78716C]">
                  <Heart className="w-3 h-3 text-[#F472B6] fill-[#F472B6]" />
                  <span>100% Hand-Painted by Kylee with Love</span>
                </div>
              </div>

            </div>

          </form>
        )}

      </div>
    </div>
  );
};
